/**
 * 🛡️ 관리자 페이지 - 사용자 구독 플랜 관리
 *
 * 관리자(ADMIN_EMAILS)만 접근 가능. 실제 권한 검증은 Cloud Functions에서 수행한다.
 * 관리자 전용 화면이므로 다국어(i18n) 없이 한국어로 고정한다.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { isAdminEmail, ADMIN_EMAILS } from '../utils/adminConfig';

interface AdminUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  providers: string[];
  disabled: boolean;
  createdAt: string | null;
  lastSignInAt: string | null;
  plan: string | null;
  subscriptionStatus: string | null;
  trialEndsAt: string | null;
  planExpiresAt: string | null;
}

// 변경 가능한 플랜 (functions/index.js 의 VALID_PLANS 와 동일하게 유지)
const PLAN_OPTIONS = [
  { value: 'free_trial', label: '무료 체험', desc: '명함 1장 · 인보이스 3건 · 기본 14일' },
  { value: 'card_only', label: '명함 전용', desc: '명함 2장 · QR 로고 · 인보이스/통계 차단' },
  { value: 'standard', label: '스탠다드', desc: '명함 5장 · 인보이스 월 8건 · 기초 통계' },
  { value: 'pro', label: '프로', desc: '전부 무제한 · 커스텀 도메인 · 고급 통계' },
  { value: 'expired', label: '만료', desc: '모든 기능 차단' },
];

const DURATION_OPTIONS = [
  { months: 1, label: '1개월' },
  { months: 3, label: '3개월' },
  { months: 6, label: '6개월' },
  { months: 12, label: '12개월' },
  { months: 120, label: '10년' },
];

const PAID_PLANS = ['card_only', 'standard', 'pro'];

const PLAN_BADGE_CLASS: Record<string, string> = {
  free_trial: 'bg-gray-100 text-gray-700',
  card_only: 'bg-blue-100 text-blue-700',
  standard: 'bg-indigo-100 text-indigo-700',
  pro: 'bg-amber-100 text-amber-800',
  expired: 'bg-red-100 text-red-700',
};

const getPlanLabel = (plan: string | null) => {
  if (!plan) return '미설정';
  return PLAN_OPTIONS.find((p) => p.value === plan)?.label || plan;
};

const formatDate = (iso: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading, initializing } = useAuth();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('free_trial');
  const [selectedMonths, setSelectedMonths] = useState(12);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // 인증 정보가 아직 준비되지 않았으면 판정을 미룬다 (성급한 권한 없음 화면 방지)
  const authReady = !initializing && !authLoading;
  const isAdmin = authReady && isAdminEmail(user?.email);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const listUsers = httpsCallable(functions, 'adminListUsers');
      const result = await listUsers({ pageSize: 1000 });
      const data = result.data as { users?: AdminUser[] };

      const fetched = data?.users || [];
      // 가입일 최신순
      fetched.sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });

      setUsers(fetched);
    } catch (err: any) {
      console.error('사용자 목록 조회 실패:', err);
      setError(err?.message || '사용자 목록을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authReady) return; // 인증 확정 전에는 아무것도 하지 않는다
    if (isAdmin) {
      loadUsers();
    } else {
      setIsLoading(false);
    }
  }, [authReady, isAdmin, loadUsers]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.displayName || '').toLowerCase().includes(q) ||
        u.uid.toLowerCase().includes(q)
    );
  }, [users, search]);

  const openModal = (target: AdminUser) => {
    setSelectedUser(target);
    setSelectedPlan(target.plan || 'free_trial');
    setSelectedMonths(12);
  };

  const closeModal = () => {
    if (isSaving) return;
    setSelectedUser(null);
  };

  const handleApplyPlan = async () => {
    if (!selectedUser) return;

    const isPaid = PAID_PLANS.includes(selectedPlan);
    const periodText = isPaid
      ? ` (${DURATION_OPTIONS.find((d) => d.months === selectedMonths)?.label || `${selectedMonths}개월`})`
      : '';

    const confirmed = window.confirm(
      `${selectedUser.email || selectedUser.uid}\n\n` +
        `${getPlanLabel(selectedUser.plan)} → ${getPlanLabel(selectedPlan)}${periodText}\n\n` +
        '이 사용자의 구독 플랜을 변경할까요?'
    );
    if (!confirmed) return;

    setIsSaving(true);
    try {
      const setUserPlan = httpsCallable(functions, 'adminSetUserPlan');
      const payload: Record<string, any> = {
        targetUid: selectedUser.uid,
        plan: selectedPlan,
        reason: '관리자 수동 변경',
      };
      if (isPaid) payload.durationMonths = selectedMonths;

      const result = await setUserPlan(payload);
      const data = result.data as { expiresAt?: string };

      // 목록 즉시 반영
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === selectedUser.uid
            ? {
                ...u,
                plan: selectedPlan,
                subscriptionStatus: selectedPlan === 'expired' ? 'expired' : 'active',
                planExpiresAt: isPaid ? data?.expiresAt || null : null,
                trialEndsAt: selectedPlan === 'free_trial' ? data?.expiresAt || null : u.trialEndsAt,
              }
            : u
        )
      );

      setSelectedUser(null);
      setToast('플랜이 변경되었습니다.');
      setTimeout(() => setToast(null), 3000);
    } catch (err: any) {
      console.error('플랜 변경 실패:', err);
      alert(`플랜 변경 실패\n\n${err?.message || '알 수 없는 오류'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ⏳ 인증 정보 확인 중
  if (!authReady) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">로그인 정보 확인 중...</p>
        </div>
      </div>
    );
  }

  // 🚫 관리자가 아닌 경우 — 어떤 계정으로 로그인했는지 함께 보여준다
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center max-w-md w-full">
          <div className="text-4xl mb-4">🔒</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">접근 권한이 없습니다</h1>

          <div className="text-left bg-gray-50 rounded-lg p-4 my-5 text-sm space-y-1">
            <div>
              <span className="text-gray-500">현재 로그인: </span>
              <span className="font-medium text-gray-900 break-all">
                {user?.email || '(로그인되지 않음)'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">필요한 관리자 계정: </span>
              <span className="font-medium text-gray-900 break-all">
                {ADMIN_EMAILS.join(', ')}
              </span>
            </div>
          </div>

          <p className="text-gray-600 mb-6 text-sm">
            관리자 계정으로 다시 로그인해 주세요.
          </p>

          <div className="flex gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              대시보드
            </button>
            <button
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              계정 바꾸기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">🛡️ 사용자 플랜 관리</h1>
              <p className="text-gray-600 text-sm">
                관리자 전용 · 로그인 계정: {user?.email}
              </p>
            </div>
            <button
              onClick={loadUsers}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {isLoading ? '불러오는 중...' : '새로고침'}
            </button>
          </div>

          {/* 검색 */}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="이메일 · 이름 · UID 검색"
              className="flex-1 min-w-[240px] px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-500">
              전체 {users.length}명{search ? ` · 검색 ${filteredUsers.length}명` : ''}
            </span>
          </div>
        </div>

        {/* 본문 */}
        {isLoading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">사용자 목록 불러오는 중...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-red-600 mb-6 whitespace-pre-wrap">{error}</p>
            <button
              onClick={loadUsers}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              다시 시도
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">사용자</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">현재 플랜</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">만료일</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">가입일</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">최근 로그인</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-700">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                        표시할 사용자가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => {
                      const expiry =
                        u.plan === 'free_trial'
                          ? u.trialEndsAt
                          : PAID_PLANS.includes(u.plan || '')
                          ? u.planExpiresAt
                          : null;

                      return (
                        <tr key={u.uid} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">
                              {u.email || '(익명 계정)'}
                            </div>
                            {u.displayName && (
                              <div className="text-gray-600 text-xs">{u.displayName}</div>
                            )}
                            <div className="text-gray-400 text-xs font-mono">{u.uid}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                PLAN_BADGE_CLASS[u.plan || ''] || 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {getPlanLabel(u.plan)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{formatDate(expiry)}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDate(u.createdAt)}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDate(u.lastSignInAt)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => openModal(u)}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700"
                            >
                              플랜 변경
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* 플랜 변경 모달 */}
      {selectedUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">플랜 변경</h2>
                  <p className="text-sm text-gray-600 mt-1 break-all">
                    {selectedUser.email || selectedUser.uid}
                  </p>
                  <p className="text-sm text-gray-500">
                    현재 플랜: {getPlanLabel(selectedUser.plan)}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  disabled={isSaving}
                  className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                >
                  ×
                </button>
              </div>

              <div className="space-y-2 mb-4">
                {PLAN_OPTIONS.map((option) => {
                  const active = selectedPlan === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setSelectedPlan(option.value)}
                      disabled={isSaving}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                        active
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`font-semibold ${active ? 'text-blue-700' : 'text-gray-900'}`}
                        >
                          {option.label}
                        </span>
                        {active && <span className="text-blue-600">✓</span>}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{option.desc}</div>
                    </button>
                  );
                })}
              </div>

              {/* 유료 플랜일 때 기간 선택 */}
              {PAID_PLANS.includes(selectedPlan) && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">구독 기간</h3>
                  <div className="flex flex-wrap gap-2">
                    {DURATION_OPTIONS.map((d) => {
                      const active = selectedMonths === d.months;
                      return (
                        <button
                          key={d.months}
                          onClick={() => setSelectedMonths(d.months)}
                          disabled={isSaving}
                          className={`px-3 py-1.5 rounded-full text-sm border ${
                            active
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={closeModal}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={handleApplyPlan}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSaving ? '변경 중...' : '이 플랜으로 변경'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 완료 토스트 */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-5 py-3 rounded-lg shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
